import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ListChecks, RefreshCw, Package, AlertCircle, Loader, Check, X, CheckCircle, Trash2, Edit3, ChevronDown, ChevronUp, Info, ArrowUpDown } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { BulkUpdateModal, BulkUpdateChanges, BulkDeliveryMethodOption, cleanDeliveryMethodDescription } from './BulkUpdateModal';
import { deliveryMethodService } from '../../../../services/deliveryMethodService';
import { filterGroupService } from '../../../../services/filterGroupService';
import { FilterGroupDto } from '../../../../types/filterGroup';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction } from '../../../shared/table';

// Extended type for table
interface PListTableRow extends TableDataRow {
  id: number;
  pListID: number;
  pListStatus: number;
  statusText: string;
  description1: string | null;
  description2: string | null;
  packageRemark: string | null;
  pListDate: string | null;
  deliveryDate: string | null;
  numOfOrderLines: number;
  weight: number | null;
  volum: number | null;
  operatorID: string | null;
  priority: number;
  pListIsBatchYesNo: boolean | null;
  filterGroupID: string | null;
  lockedBy: string | null;
  deliveryMethodNo: number | null;
  deliveryMethodDescription: string | null;
  // Keep original for updates
  _original: PListDto;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<PListTableRow>();

// Editable field types for inline editing
type EditableTextField = 'operatorID' | 'packageRemark' | 'filterGroupID';
type EditableNumberField = 'priority' | 'weight' | 'volum';
type EditableField = EditableTextField | EditableNumberField;

// Livssyklus-/eierskapsfelt eies av de dedikerte plukk-endepunktene (start/complete/lock/unlock).
// Manager-redigering av metadata (prioritet/operatør m.m.) skal ALDRI sende disse — en utdatert
// snapshot kunne ellers dra et fullført plukk bakover (lost update: status 60 -> 20 + gjenopprettet lås).
const PLIST_LIFECYCLE_FIELDS = ['pListStatus', 'lockStatus', 'lockedBy', 'startWorkDateTime', 'stopWorkDateTime'] as const;

// Bygger en metadata-oppdatering fra originalraden + endrede felt, men utelater livssyklus-feltene.
const buildMetadataUpdate = (original: PListDto, changes: Record<string, unknown>): Record<string, unknown> => {
  const payload: Record<string, unknown> = { ...original, ...changes };
  for (const f of PLIST_LIFECYCLE_FIELDS) {
    delete payload[f];
  }
  return payload;
};

interface EditableCellState {
  [pListId: number]: {
    priority?: number;
    operatorID?: string;
    packageRemark?: string;
    weight?: number;
    volum?: number;
    filterGroupID?: string;
  };
}

interface NewPickingListsProps {
  onBack?: () => void;
}

const NewPickingLists: React.FC<NewPickingListsProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Expanded state and details
  const [listDetails, setListDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editState, setEditState] = useState<EditableCellState>({});
  const [savingPLists, setSavingPLists] = useState<Set<number>>(new Set());
  const [savedPLists, setSavedPLists] = useState<Set<number>>(new Set());

  // Selection state
  const [selectedPListIds, setSelectedPListIds] = useState<number[]>([]);

  // FilterGroup state
  const [filterGroups, setFilterGroups] = useState<FilterGroupDto[]>([]);

  // Approval state
  const [approvingPLists, setApprovingPLists] = useState(false);
  const [showApprovalResult, setShowApprovalResult] = useState(false);
  const [approvalResult, setApprovalResult] = useState<any>(null);

  // Delete state
  const [deletingPListId, setDeletingPListId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pListToDelete, setPListToDelete] = useState<PListDto | null>(null);

  // Bulk update state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [deliveryMethods, setDeliveryMethods] = useState<BulkDeliveryMethodOption[]>([]);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Refs to access current state in stable callbacks (prevents columns array from being recreated)
  const editStateRef = useRef<EditableCellState>(editState);
  const pickingListsRef = useRef<PListDto[]>(pickingLists);

  // Keep refs in sync with state
  useEffect(() => {
    editStateRef.current = editState;
  }, [editState]);

  useEffect(() => {
    pickingListsRef.current = pickingLists;
  }, [pickingLists]);

  useEffect(() => {
    loadPickingLists();
    loadFilterGroups();
    loadDeliveryMethods();
  }, []);

  // Leveringsmåter for bulk-modalens nedtrekk. getCurrent() gir rå Description; rydd for visning.
  const loadDeliveryMethods = async () => {
    try {
      const methods = await deliveryMethodService.getCurrent();
      setDeliveryMethods(
        methods
          .map(m => ({ deliveryMethodNo: m.deliveryMethodNo, description: cleanDeliveryMethodDescription(m.description) }))
          .sort((a, b) => a.deliveryMethodNo - b.deliveryMethodNo)
      );
    } catch (err) {
      console.error('Kunne ikke laste leveringsmåter:', err);
    }
  };

  const loadFilterGroups = async () => {
    try {
      const groups = await filterGroupService.getCurrent();
      setFilterGroups(groups);
    } catch (err) {
      console.error('Error loading filter groups:', err);
    }
  };

  const loadPickingLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pickingService.getPickingListsByStatus(0, currentWarehouse || undefined); // Status 0 = Nye
      setPickingLists(data);

      // Initialize edit state
      const newEditState: EditableCellState = {};
      data.forEach(pl => {
        newEditState[pl.pListID] = {
          priority: pl.priority,
          operatorID: pl.operatorID || ''
        };
      });
      setEditState(newEditState);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.loadFailed', {}, 'Feil ved lasting av plukklister'));
      console.error('Error loading picking lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPickingListDetails = async (pListId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(pListId));
      const details = await pickingService.getPickingDetails(pListId);
      setListDetails(prev => new Map(prev).set(pListId, details));
    } catch (err) {
      console.error(`Error loading details for picking list ${pListId}:`, err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  };

  // Transform to table data
  const tableData: PListTableRow[] = useMemo(() => {
    return pickingLists.map(pl => ({
      id: pl.pListID,
      pListID: pl.pListID,
      pListStatus: pl.pListStatus,
      statusText: pl.statusText,
      description1: pl.description1 || null,
      description2: pl.description2 || null,
      packageRemark: pl.packageRemark || null,
      pListDate: pl.pListDate || null,
      deliveryDate: pl.deliveryDate || null,
      numOfOrderLines: pl.numOfOrderLines || 0,
      weight: pl.weight ?? null,
      volum: pl.volum ?? null,
      operatorID: pl.operatorID || null,
      priority: pl.priority,
      pListIsBatchYesNo: pl.pListIsBatchYesNo ?? null,
      filterGroupID: pl.filterGroupID || null,
      lockedBy: pl.lockedBy || null,
      deliveryMethodNo: pl.deliveryMethodNo ?? null,
      deliveryMethodDescription: pl.deliveryMethodDescription ?? null,
      _original: pl
    }));
  }, [pickingLists]);

  // Row ID function
  const getRowId = useCallback((row: PListTableRow) => row.pListID, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedRows: (string | number)[], selectedData: PListTableRow[]) => {
    setSelectedPListIds(selectedRows as number[]);
  }, []);

  // Save inline edit for a specific field - uses refs for stable callback
  const saveInlineEdit = useCallback(async (
    pListId: number,
    field?: EditableField,
    newFieldValue?: string | number
  ) => {
    const editData = editStateRef.current[pListId];
    const originalPickList = pickingListsRef.current.find(p => p.pListID === pListId);
    if (!originalPickList) return;

    // If a specific field is provided, save just that field
    if (field) {
      let newValue: string | number | undefined;
      if (newFieldValue !== undefined) {
        newValue = newFieldValue;
      } else if (editData) {
        newValue = editData[field] as string | number | undefined;
      } else {
        return;
      }

      // Check if value actually changed
      const oldValue = (originalPickList as any)[field] ?? '';
      if (String(newValue) === String(oldValue)) return;

      try {
        setSavingPLists(prev => new Set(prev).add(pListId));

        const pickListUpdate = buildMetadataUpdate(originalPickList, {
          [field]: newValue === '' ? null : newValue
        });

        await pickingService.updatePickingList(pListId, pickListUpdate);

        // Update local state
        setPickingLists(prev => prev.map(p =>
          p.pListID === pListId ? { ...p, [field]: newValue === '' ? null : newValue } : p
        ));

        // Show saved indicator
        setSavedPLists(prev => new Set(prev).add(pListId));
        setTimeout(() => {
          setSavedPLists(prev => {
            const newSet = new Set(prev);
            newSet.delete(pListId);
            return newSet;
          });
        }, 2000);
      } catch (err) {
        console.error('Feil ved lagring:', err);
        alert(`${t('manager.modules.newPickingLists.errors.saveFailed', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
      } finally {
        setSavingPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }
      return;
    }

    // Legacy: save all changed fields at once (for priority/operatorID backward compat)
    if (!editData) return;

    if (editData.priority === originalPickList.priority &&
        editData.operatorID === (originalPickList.operatorID || '')) {
      return;
    }

    try {
      setSavingPLists(prev => new Set(prev).add(pListId));

      const pickListUpdate = buildMetadataUpdate(originalPickList, {
        priority: editData.priority ?? originalPickList.priority,
        operatorID: editData.operatorID !== undefined ? editData.operatorID : originalPickList.operatorID
      });

      await pickingService.updatePickingList(pListId, pickListUpdate);

      // Update local state — kun de faktisk endrede metadata-feltene (ikke livssyklus).
      setPickingLists(prev => prev.map(p =>
        p.pListID === pListId
          ? {
              ...p,
              priority: editData.priority ?? p.priority,
              operatorID: editData.operatorID !== undefined ? editData.operatorID : p.operatorID
            }
          : p
      ));

      // Show saved indicator
      setSavedPLists(prev => new Set(prev).add(pListId));
      setTimeout(() => {
        setSavedPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }, 2000);

    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`${t('manager.modules.newPickingLists.errors.saveFailed', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setSavingPLists(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  }, [t]); // Uses refs for state; t for translated error messages

  // Inline editable priority cell - stable callback using refs
  const renderPriorityCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <input
          key={`priority-${pListId}-${value}`}
          type="number"
          defaultValue={value}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], priority: newValue }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'priority')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'priority');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-16 px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
        />
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Inline editable operator cell - stable callback using refs
  const renderOperatorCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <input
          key={`operator-${pListId}-${value}`}
          type="text"
          defaultValue={value || ''}
          onChange={(e) => {
            e.stopPropagation();
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], operatorID: e.target.value }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'operatorID')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'operatorID');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          placeholder="-"
        />
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Generic renderer for editable text cells
  const renderEditableText = useCallback((field: EditableTextField) => {
    return (value: string | null, row: PListTableRow) => {
      const pListId = row.pListID;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingPLists.has(pListId);
      const isSaved = savedPLists.has(pListId);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${pListId}-${value}`}
            type="text"
            defaultValue={value || ''}
            onChange={(e) => {
              e.stopPropagation();
              setEditState(prev => ({
                ...prev,
                [pListId]: { ...prev[pListId], [field]: e.target.value }
              }));
            }}
            onBlur={() => saveInlineEdit(pListId, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(pListId, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Generic renderer for editable number cells
  const renderEditableNumber = useCallback((field: EditableNumberField) => {
    return (value: number | null, row: PListTableRow) => {
      const pListId = row.pListID;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingPLists.has(pListId);
      const isSaved = savedPLists.has(pListId);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${pListId}-${value}`}
            type="number"
            step="any"
            defaultValue={value ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const numVal = e.target.value === '' ? '' : parseFloat(e.target.value);
              setEditState(prev => ({
                ...prev,
                [pListId]: { ...prev[pListId], [field]: numVal }
              }));
            }}
            onBlur={() => saveInlineEdit(pListId, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(pListId, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // FilterGroup dropdown renderer
  const renderFilterGroupDropdown = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <select
          key={`filterGroup-${pListId}-${value}-${filterGroups.length}`}
          defaultValue={value || ''}
          onChange={(e) => {
            e.stopPropagation();
            saveInlineEdit(pListId, 'filterGroupID', e.target.value);
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
        >
          <option value="">{t('manager.modules.newPickingLists.table.noFilterGroup', {}, '— Ingen —')}</option>
          {filterGroups.map(fg => (
            <option key={fg.filterGroupID} value={fg.filterGroupID}>
              {fg.filterGroupID} - {fg.filterGroupDescr}
            </option>
          ))}
        </select>
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit, filterGroups, t]);

  // Row actions (Handling column)
  const rowActions: RowAction<PListTableRow>[] = useMemo(() => [
    {
      id: 'delete-plist',
      label: t('manager.modules.newPickingLists.actions.deletePickingList', {}, 'Slett plukkliste'),
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: PListTableRow) => !!row.lockedBy,
      onClick: (row: PListTableRow) => {
        setPListToDelete(row._original);
        setShowDeleteConfirm(true);
      }
    }
  ], [t]);

  // Kode-baserte, språk-bevisste filteropsjoner for status-kolonnen (StatusType 1).
  const pListStatusOptions = useStatusOptions(1);

  // Define columns based on legacy PlistNew.dfm
  const columns: ColumnDefinition<PListTableRow>[] = useMemo(() => [
    columnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.newPickingLists.table.pListNo', {}, 'Plukklistenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value !== null && value !== undefined ? String(value) : '-'
    }),
    columnFactory.createTextColumn('pListStatus', 'pListStatus', t('common.fields.status', {}, 'Status'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: pListStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.pListStatus} statusType={1} />
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('description2', 'description2', t('manager.modules.newPickingLists.table.description2', {}, 'Beskrivelse 2'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('packageRemark', 'packageRemark', t('manager.modules.newPickingLists.table.packageRemark', {}, 'Pakkekommentar'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableText('packageRemark')
    }),
    columnFactory.createDateColumn('pListDate', 'pListDate', t('manager.modules.newPickingLists.table.createdDate', {}, 'Opprettet dato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.newPickingLists.table.deliveryDate', {}, 'Leveringsdato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLines', 'numOfOrderLines', t('manager.modules.newPickingLists.table.numLines', {}, 'Ant. linjer'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('weight', 'weight', t('warehouse.general.weight', {}, 'Vekt'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableNumber('weight')
    }),
    columnFactory.createNumberColumn('volum', 'volum', t('manager.modules.newPickingLists.table.volume', {}, 'Volum'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableNumber('volum')
    }),
    columnFactory.createNumberColumn('priority', 'priority', t('manager.modules.newPickingLists.table.priority', {}, 'Prioritet'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderPriorityCell
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.newPickingLists.table.operator', {}, 'Operatør'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      renderer: renderOperatorCell
    }),
    columnFactory.createTextColumn('pListIsBatchYesNo', 'pListIsBatchYesNo', t('manager.modules.newPickingLists.table.batchPick', {}, 'Samplukk'), {
      width: 90,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', t('manager.modules.newPickingLists.table.filterGroup', {}, 'Filtergruppe'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      renderer: renderFilterGroupDropdown
    }),
    // Leveringsmåte: viser DeliveryMethods.Description (slått opp via DeliveryMethodNo i backend),
    // med fallback til selve nummeret hvis leveringsmåten mangler beskrivelse.
    columnFactory.createTextColumn('deliveryMethodNo', 'deliveryMethodDescription', t('manager.modules.newPickingLists.table.deliveryMethod', {}, 'Leveringsmetode'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value, row) => value || (row.deliveryMethodNo !== null ? row.deliveryMethodNo!.toString() : '-')
    }),
    columnFactory.createTextColumn('lockedBy', 'lockedBy', t('manager.modules.newPickingLists.table.lockedBy', {}, 'Last av'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: (value) => value ? (
        <span className="text-yellow-600 text-xs flex items-center">
          <AlertCircle size={12} className="mr-1" />
          {value}
        </span>
      ) : <span className="text-gray-400 text-xs">-</span>
    }),
  ], [pListStatusOptions, renderPriorityCell, renderOperatorCell, renderEditableText, renderEditableNumber, renderFilterGroupDropdown, t]);


  // Approval handler
  const handleApprovePLists = async () => {
    if (selectedPListIds.length === 0) {
      alert(t('manager.modules.newPickingLists.approval.selectAtLeastOne', {}, 'Vennligst velg minst én plukkliste å godkjenne'));
      return;
    }

    const confirmMessage = selectedPListIds.length > 1
      ? t('manager.modules.newPickingLists.approval.confirmPlural', { '0': selectedPListIds.length }, `Er du sikker på at du vil godkjenne ${selectedPListIds.length} plukklister?`)
      : t('manager.modules.newPickingLists.approval.confirmSingular', { '0': selectedPListIds.length }, `Er du sikker på at du vil godkjenne ${selectedPListIds.length} plukkliste?`);
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setApprovingPLists(true);

      // Load details for selected PLists to get order numbers
      const orderNumbersSet = new Set<number>();

      for (const pListId of selectedPListIds) {
        try {
          const details = await pickingService.getPickingDetails(pListId);
          details.lines.forEach(line => {
            if (line.orderNo) {
              orderNumbersSet.add(line.orderNo);
            }
          });
        } catch (err) {
          console.error(`Error loading details for PList ${pListId}:`, err);
        }
      }

      const orderNumbers = Array.from(orderNumbersSet);

      if (orderNumbers.length === 0) {
        alert(t('manager.modules.newPickingLists.approval.noOrdersFound', {}, 'Ingen ordre funnet for valgte plukklister'));
        return;
      }

      const result = await pickingService.batchApprovePListsByOrders(orderNumbers);

      setApprovalResult(result);
      setShowApprovalResult(true);
      setSelectedPListIds([]);

      await loadPickingLists();

    } catch (err) {
      console.error('Error approving PLists:', err);
      alert(`${t('manager.modules.newPickingLists.approval.approvalFailed', {}, 'Feil ved godkjenning')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setApprovingPLists(false);
    }
  };

  // Bulk update — loops the existing per-list endpoints for each selected pick. Only fields the
  // manager enabled in the modal are applied. Prioritet/leveringsdato → PList (updatePickingList);
  // leveringsmåte → PListOrders (updatePickingListDeliveryMethod) for alle ordrer på plukket.
  const handleBulkUpdate = useCallback(async (changes: BulkUpdateChanges) => {
    if (selectedPListIds.length === 0) return;

    const updatesPList = changes.priority !== undefined || changes.deliveryDate !== undefined;
    const updateDeliveryMethod = changes.deliveryMethodNo !== undefined;
    if (!updatesPList && !updateDeliveryMethod) return;

    setBulkSaving(true);
    setBulkProgress({ done: 0, total: selectedPListIds.length });

    const succeeded: number[] = [];
    const failed: number[] = [];

    // Sequential so progress is meaningful and the backend isn't hit with a large parallel burst
    for (let i = 0; i < selectedPListIds.length; i++) {
      const pListId = selectedPListIds[i];
      const original = pickingListsRef.current.find(p => p.pListID === pListId);
      try {
        if (updatesPList && original) {
          const fieldChanges: Record<string, unknown> = {};
          if (changes.priority !== undefined) fieldChanges.priority = changes.priority;
          if (changes.deliveryDate !== undefined) fieldChanges.deliveryDate = changes.deliveryDate;
          // buildMetadataUpdate utelater livssyklus-felt (status/lås/arbeidstid) for å unngå
          // lost-update fra et utdatert snapshot — samme vern som inline-redigering.
          await pickingService.updatePickingList(pListId, buildMetadataUpdate(original, fieldChanges));
        }
        if (updateDeliveryMethod) {
          await pickingService.updatePickingListDeliveryMethod(pListId, changes.deliveryMethodNo!);
        }
        succeeded.push(pListId);
      } catch (err) {
        console.error(`Error bulk-updating PList ${pListId}:`, err);
        failed.push(pListId);
      }
      setBulkProgress({ done: i + 1, total: selectedPListIds.length });
    }

    // Patch local state for successful updates (consistent with inline edit — no full reload)
    const succeededSet = new Set(succeeded);
    const dmDescription = updateDeliveryMethod
      ? (deliveryMethods.find(d => d.deliveryMethodNo === changes.deliveryMethodNo)?.description ?? null)
      : null;
    setPickingLists(prev => prev.map(p => {
      if (!succeededSet.has(p.pListID)) return p;
      const patched: PListDto = { ...p };
      if (changes.priority !== undefined) patched.priority = changes.priority;
      if (changes.deliveryDate !== undefined) patched.deliveryDate = changes.deliveryDate;
      if (updateDeliveryMethod) {
        patched.deliveryMethodNo = changes.deliveryMethodNo!;
        patched.deliveryMethodDescription = dmDescription;
      }
      return patched;
    }));
    if (changes.priority !== undefined) {
      const newPriority = changes.priority;
      setEditState(prev => {
        const next = { ...prev };
        succeeded.forEach(id => {
          if (next[id]) next[id] = { ...next[id], priority: newPriority };
        });
        return next;
      });
    }

    setBulkSaving(false);
    setBulkProgress(null);
    setShowBulkModal(false);
    setSelectedPListIds([]);

    if (failed.length > 0) {
      alert(t('manager.modules.newPickingLists.bulkUpdate.partialFailure', { '0': failed.length, '1': failed.join(', ') }, `Kunne ikke oppdatere ${failed.length} plukkliste(r): ${failed.join(', ')}`));
    }
  }, [selectedPListIds, deliveryMethods, t]);

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!pListToDelete) return;

    try {
      setDeletingPListId(pListToDelete.pListID);
      setShowDeleteConfirm(false);

      const result = await pickingService.deletePickingList(pListToDelete.pListID);

      if (result.success) {
        setPickingLists(prev => prev.filter(p => p.pListID !== pListToDelete.pListID));
        alert(result.message);
      }
    } catch (err) {
      console.error('Error deleting PList:', err);
      alert(`${t('manager.modules.newPickingLists.delete.deleteFailed', {}, 'Feil ved sletting')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setDeletingPListId(null);
      setPListToDelete(null);
    }
  };

  // Detail renderer for expandable rows
  const renderPListLines = useCallback((pList: PListTableRow, details: PListDetailsDto | undefined) => {
    const isLoadingDetails = loadingDetails.has(pList.pListID);

    if (isLoadingDetails) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">{t('manager.modules.newPickingLists.lines.loadingLines', {}, 'Laster linjer...')}</span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          {t('manager.modules.newPickingLists.lines.couldNotLoadLines', {}, 'Kunne ikke laste linjer')}
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>{t('manager.modules.newPickingLists.lines.linesForList', { '0': pList.pListID }, `Plukkliste-linjer for ${pList.pListID}`)}</span>
          <span className="text-xs text-gray-500">
            ({t('manager.modules.newPickingLists.lines.completedSummary', { '0': details.completedLines, '1': details.totalLines, '2': details.completionPercentage.toFixed(0) }, `${details.completedLines} av ${details.totalLines} fullført - ${details.completionPercentage.toFixed(0)}%`)})
          </span>
        </h4>

        {details.lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.lineNo', {}, 'Linjenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.orderNo', {}, 'Ordrenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.orderLine', {}, 'Ordre linje')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.itemNo', {}, 'Varenr')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('common.fields.quantity', {}, 'Antall')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('warehouse.general.reserved', {}, 'Reservert')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.picked', {}, 'Plukket')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.remaining', {}, 'Gjenstående')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.status', {}, 'Status')}</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.completed', {}, 'Fullført')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {details.lines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900 font-medium">{line.pListLineNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.orderNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyReserved.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyPicked.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.remainingQty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900">{line.binCode || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.warehouseID || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineStatus}</td>
                  <td className="py-2 px-3 text-center">
                    {line.isCompleted ? (
                      <Check className="text-green-600 mx-auto" size={16} />
                    ) : (
                      <X className="text-gray-400 mx-auto" size={16} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t('manager.modules.newPickingLists.lines.noLinesFound', {}, 'Ingen linjer funnet for denne plukklisten')}
          </div>
        )}
      </div>
    );
  }, [loadingDetails, t]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<PListTableRow, PListDetailsDto | undefined> = useMemo(() => ({
    getDetailData: (pList) => listDetails.get(pList.pListID),
    renderDetail: renderPListLines,
    expandOnRowClick: false,
    onExpand: async (pList) => {
      if (!listDetails.has(pList.pListID)) {
        await loadPickingListDetails(pList.pListID);
      }
    }
  }), [listDetails, renderPListLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'approve',
      label: t('manager.modules.newPickingLists.actions.approve', { '0': selectedPListIds.length }, `Godkjenn (${selectedPListIds.length})`),
      icon: CheckCircle,
      onClick: handleApprovePLists,
      disabled: () => approvingPLists || selectedPListIds.length === 0,
      variant: 'primary' as const
    },
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: loadPickingLists,
      variant: 'secondary' as const
    }
  ], [selectedPListIds.length, approvingPLists, handleApprovePLists, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>{t('manager.modules.newPickingLists.loadingPickingLists', {}, 'Laster plukklister...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">{t('manager.modules.newPickingLists.errors.loadFailed', {}, 'Feil ved lasting av plukklister')}</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadPickingLists}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>{t('manager.modules.newPickingLists.tryAgain', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <ListChecks size={24} />
            <span>{t('manager.modules.newPickingLists.title', {}, 'Nye Plukklister')}</span>
          </h1>
          <span className="text-sm text-gray-500">
            {t('manager.modules.newPickingLists.listCount', { '0': tableData.length }, `${tableData.length} plukklister`)}
          </span>
        </div>
        {/* Info toggle */}
        <button
          type="button"
          onClick={() => setShowInfo(prev => !prev)}
          className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
          aria-expanded={showInfo}
        >
          <Info size={14} />
          <span>{t('manager.modules.newPickingLists.info.toggleLabel', {}, 'Hva er Nye Plukklister?')}</span>
          {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showInfo && (
          <div className="mt-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800">
            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.purposeHeading', {}, 'Hensikt')}</h3>
              <p>
                {t('manager.modules.newPickingLists.info.purposeBody', {}, 'Nye Plukklister viser plukklister som regelmotoren har generert, men som ennå ikke er godkjent eller startet av en operatør. Herfra kan en leder godkjenne, redigere prioritet og operatør-tildeling, eller slette plukklister før de slippes ut til lagermedarbeiderne.')}
              </p>
            </div>

            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.criteriaHeading', {}, 'Utvalgskriterier')}</h3>
              <p className="mb-1">{t('manager.modules.newPickingLists.info.criteriaIntro', {}, 'En plukkliste vises i listen når:')}</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 0</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.unprocessed', {}, 'Ubehandlet')}</strong>{t('manager.modules.newPickingLists.info.criteriaStatus0Suffix', {}, ' — regelmotor-generert, ikke godkjent ennå')})
                </li>
                <li>
                  {t('manager.modules.newPickingLists.info.criteriaWarehousePrefix', {}, 'Minst én av plukklistens linjer tilhører ')}<strong>{t('manager.modules.newPickingLists.info.selectedWarehouse', {}, 'valgt lager')}</strong>{t('manager.modules.newPickingLists.info.criteriaWarehouseSuffix', {}, ' (filteret øverst, f.eks. "Hovedlager")')}
                </li>
              </ol>
              <p className="mt-1">
                {t('manager.modules.newPickingLists.info.sortedByPrefix', {}, 'Sortert på ')}<code className="bg-blue-100 px-1 rounded text-xs">Priority</code>{t('manager.modules.newPickingLists.info.sortedByThen', {}, ', deretter')}{' '}
                <code className="bg-blue-100 px-1 rounded text-xs">PListID</code>{t('manager.modules.newPickingLists.info.sortedBySuffix', {}, '. Plukklister kan endres inline — filtergruppe, pakkekommentar, prioritet, vekt og volum.')}
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.cautionHeading', {}, 'Pass på')}</h3>
              <ul className="list-disc pl-5 space-y-0.5 text-gray-800">
                <li>
                  {t('manager.modules.newPickingLists.info.cautionMultiWarehouse', {}, 'Plukklister med linjer på flere lagre vises i hvert lager hvor de har minst én linje')}
                </li>
                <li>
                  {t('manager.modules.newPickingLists.info.cautionApprovePrefix', {}, 'Når en plukkliste godkjennes går den til status')}{' '}
                  <code className="bg-yellow-100 px-1 rounded text-xs">{t('manager.modules.newPickingLists.info.status10Approved', {}, '10 (Godkjent)')}</code>{t('manager.modules.newPickingLists.info.cautionApproveSuffix', {}, ' og flyttes ut av denne listen — den dukker da opp i operatørenes plukk-app')}
                </li>
                <li>
                  {t('manager.modules.newPickingLists.info.cautionDelete', {}, 'Sletting krever at plukklisten ikke er låst og ingen linjer er plukket. Tilhørende ordrelinjer "frigjøres" og dukker opp i "Nye Ordre" igjen — klar for ny regelmotorkjøring')}
                </li>
                <li>
                  {t('manager.modules.newPickingLists.info.statusTransitionsLabel', {}, 'Status-overganger:')}{' '}
                  <code className="bg-yellow-100 px-1 rounded text-xs">
                    0 (Ubehandlet) → 10 (Godkjent) → 20 (Under arbeid) → 40/45 (Plukket) → 50 (Pakket) → 60 (Avsluttet)
                  </code>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            },
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'new-picking-lists-table'
          }}
          selectedRows={selectedPListIds}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          rowActions={rowActions}
          tableActions={tableActions}
          toolbarRight={
            <button
              onClick={() => setShowBulkModal(true)}
              disabled={selectedPListIds.length === 0}
              className="px-3 py-2 text-xs font-semibold text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <ArrowUpDown className="w-4 h-4" />
              {selectedPListIds.length > 0
                ? t('manager.modules.newPickingLists.bulkUpdate.buttonWithCount', { '0': selectedPListIds.length }, `Oppdater valgte (${selectedPListIds.length})`)
                : t('manager.modules.newPickingLists.bulkUpdate.button', {}, 'Oppdater valgte')}
            </button>
          }
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label={t('manager.modules.newPickingLists.tableAriaLabel', {}, 'Nye plukklister tabell')}
        />
      </div>

      {/* Approval Result Modal */}
      {showApprovalResult && approvalResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle size={24} />
                <h2 className="text-xl font-semibold">{t('manager.modules.newPickingLists.approvalResult.title', {}, 'Godkjenning Fullført')}</h2>
              </div>
              <button
                onClick={() => setShowApprovalResult(false)}
                className="text-white hover:bg-green-700 rounded p-1"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-blue-600 text-sm font-medium">{t('manager.modules.newPickingLists.approvalResult.totalProcessed', {}, 'Totalt Behandlet')}</div>
                  <div className="text-2xl font-bold text-blue-900">{approvalResult.totalProcessed || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-green-600 text-sm font-medium">{t('manager.modules.newPickingLists.approvalResult.approved', {}, 'Godkjent')}</div>
                  <div className="text-2xl font-bold text-green-900">{approvalResult.successCount || 0}</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-yellow-600 text-sm font-medium">{t('manager.modules.newPickingLists.approvalResult.skipped', {}, 'Hoppet Over')}</div>
                  <div className="text-2xl font-bold text-yellow-900">{approvalResult.skippedCount || 0}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-red-600 text-sm font-medium">{t('manager.modules.newPickingLists.approvalResult.failed', {}, 'Feilet')}</div>
                  <div className="text-2xl font-bold text-red-900">{approvalResult.failedCount || 0}</div>
                </div>
              </div>

              {/* Detailed Results */}
              {approvalResult.results && approvalResult.results.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('manager.modules.newPickingLists.approvalResult.detailedResult', {}, 'Detaljert Resultat')}</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.approvalResult.pListIdHeader', {}, 'PList ID')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.approvalResult.orderHeader', {}, 'Ordre')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('common.fields.status', {}, 'Status')}</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.newPickingLists.approvalResult.messageHeader', {}, 'Melding')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {approvalResult.results.map((result: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{result.pListId}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{result.orderNo || '-'}</td>
                            <td className="px-4 py-2 text-sm">
                              {result.success ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Check size={12} className="mr-1" />
                                  {t('manager.modules.newPickingLists.approvalResult.approved', {}, 'Godkjent')}
                                </span>
                              ) : result.skipped ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {t('manager.modules.newPickingLists.approvalResult.skipped', {}, 'Hoppet Over')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <X size={12} className="mr-1" />
                                  {t('manager.modules.newPickingLists.approvalResult.failed', {}, 'Feilet')}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{result.message || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {approvalResult.errors && approvalResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold mb-2">{t('manager.modules.newPickingLists.approvalResult.errorsHeading', {}, 'Feil')}</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {approvalResult.errors.map((error: string, index: number) => (
                      <li key={index} className="text-red-700 text-sm">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowApprovalResult(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                {t('common.buttons.close', {}, 'Lukk')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && pListToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">{t('manager.modules.newPickingLists.delete.confirmTitle', {}, 'Bekreft sletting')}</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t('manager.modules.newPickingLists.delete.confirmQuestionPrefix', {}, 'Er du sikker på at du vil slette plukkliste ')}<strong>{pListToDelete.pListID}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.descriptionLabel', {}, 'Beskrivelse:')}</div>
                  <div className="text-gray-900">{pListToDelete.description1 || '-'}</div>
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.numLinesLabel', {}, 'Antall linjer:')}</div>
                  <div className="text-gray-900">{pListToDelete.numOfOrderLines || 0}</div>
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.statusLabel', {}, 'Status:')}</div>
                  <div className="text-gray-900">{pListToDelete.statusText}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>{t('manager.modules.newPickingLists.delete.warningLabel', {}, 'Advarsel:')}</strong> {t('manager.modules.newPickingLists.delete.warningBody', {}, 'Denne handlingen kan ikke angres. Plukklisten og alle tilhørende linjer vil bli permanent slettet.')}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPListToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>{t('manager.modules.newPickingLists.actions.deletePickingList', {}, 'Slett plukkliste')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal (prioritet / leveringsdato / leveringsmåte) */}
      <BulkUpdateModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedCount={selectedPListIds.length}
        isSaving={bulkSaving}
        progress={bulkProgress}
        deliveryMethods={deliveryMethods}
        onConfirm={handleBulkUpdate}
      />
    </div>
  );
};

export default NewPickingLists;
